import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { FaPlay, FaImage, FaVideo } from 'react-icons/fa';

const MAX_CONCURRENT_LOADS = 4;
let activeLoads = 0;
const loadQueue = [];

const processQueue = () => {
  while (loadQueue.length > 0 && activeLoads < MAX_CONCURRENT_LOADS) {
    const nextLoad = loadQueue.shift();
    if (nextLoad) {
      activeLoads++;
      nextLoad();
    }
  }
};

const LazyImage = ({ src, dataSrc, alt, style, onLoad, onError, className, crossOrigin }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const imgRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '100px', threshold: 0.1 }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible || imageSrc) return;

    const loadImage = () => {
      setImageSrc(src);
    };

    if (activeLoads < MAX_CONCURRENT_LOADS) {
      activeLoads++;
      loadImage();
    } else {
      loadQueue.push(loadImage);
    }
  }, [isVisible, src, imageSrc]);

  const handleLoad = (e) => {
    setIsLoaded(true);
    activeLoads = Math.max(0, activeLoads - 1);
    processQueue();
    if (onLoad) onLoad(e);
  };

  const handleError = (e) => {
    activeLoads = Math.max(0, activeLoads - 1);
    processQueue();
    if (onError) onError(e);
  };

  return (
    <div ref={imgRef} style={{ ...style, position: 'relative' }}>
      {!isLoaded && (
        <div 
          style={{ 
            position: 'absolute', 
            inset: 0, 
            background: 'linear-gradient(135deg, #f0f0f0 0%, #e0e0e0 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: style?.borderRadius || 4
          }}
        >
          <div style={{ 
            width: 24, 
            height: 24, 
            border: '2px solid #ccc',
            borderTopColor: '#666',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
        </div>
      )}
      {imageSrc && (
        <img
          src={imageSrc}
          data-original={dataSrc}
          data-viewer-image="true"
          alt={alt}
          className={className}
          style={{ 
            ...style, 
            opacity: isLoaded ? 1 : 0,
            transition: 'opacity 0.3s ease'
          }}
          crossOrigin={crossOrigin}
          onLoad={handleLoad}
          onError={handleError}
        />
      )}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

const VIDEO_EXTENSIONS = ['mp4', 'mov', 'avi', 'webm', 'm4v', 'mkv', '3gp'];
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'heic', 'heif'];

const getFileExtension = (media) => {
  if (media.filename) {
    const cleanFilename = media.filename.split('?')[0];
    return cleanFilename.split('.').pop().toLowerCase();
  }
  
  const path = media.path || media.image_path || media.image_url || '';
  const cleanPath = path.split('?')[0];
  const filename = cleanPath.split('/').pop() || '';
  return filename.split('.').pop().toLowerCase();
};

const isVideo = (media) => {
  const ext = getFileExtension(media);
  return VIDEO_EXTENSIONS.includes(ext);
};

const isImage = (media) => {
  const ext = getFileExtension(media);
  return IMAGE_EXTENSIONS.includes(ext);
};

const isToday = (dateStr) => {
  if (!dateStr) return false;

  try {
    let adjustedDateStr = dateStr;
    if (!dateStr.includes('+') && !dateStr.includes('Z')) {
      adjustedDateStr = dateStr + 'Z';
    }

    const d = new Date(adjustedDateStr);

    if (isNaN(d.getTime())) {
      return false;
    }

    const now = new Date();

    const torontoDateParts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Toronto',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(d);

    const torontoNowParts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Toronto',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(now);

    const torontoDate = torontoDateParts.find(part => part.type === 'year').value + '-' +
      torontoDateParts.find(part => part.type === 'month').value + '-' +
      torontoDateParts.find(part => part.type === 'day').value;

    const torontoToday = torontoNowParts.find(part => part.type === 'year').value + '-' +
      torontoNowParts.find(part => part.type === 'month').value + '-' +
      torontoNowParts.find(part => part.type === 'day').value;

    return torontoDate === torontoToday;
  } catch (error) {
    return false;
  }
};

const VideoPlayerModal = ({ videoUrl, onClose }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadProgress, setLoadProgress] = useState(0);
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && !isFullscreen) onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose, isFullscreen]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;

    const fetchVideo = async () => {
      try {
        const response = await fetch(videoUrl);
        if (!response.ok) throw new Error('Failed to fetch video');
        
        const contentLength = response.headers.get('content-length');
        const total = contentLength ? parseInt(contentLength, 10) : 0;
        
        if (!total) {
          const blob = await response.blob();
          if (!cancelled) {
            objectUrl = URL.createObjectURL(blob);
            setBlobUrl(objectUrl);
            setLoadProgress(100);
            setIsLoading(false);
          }
          return;
        }

        const reader = response.body.getReader();
        const chunks = [];
        let loaded = 0;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          if (cancelled) return;
          
          chunks.push(value);
          loaded += value.length;
          const progress = Math.round((loaded / total) * 100);
          setLoadProgress(progress);
        }

        if (!cancelled) {
          const blob = new Blob(chunks);
          objectUrl = URL.createObjectURL(blob);
          setBlobUrl(objectUrl);
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Video fetch error:', err);
        if (!cancelled) {
          setBlobUrl(videoUrl);
          setIsLoading(false);
        }
      }
    };

    fetchVideo();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [videoUrl]);

  const toggleFullscreen = async () => {
    if (!containerRef.current) return;
    
    try {
      if (!document.fullscreenElement) {
        await containerRef.current.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.error('Fullscreen error:', err);
    }
  };

  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (loadProgress / 100) * circumference;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[9999]"
      onClick={onClose}
    >
      <div 
        ref={containerRef}
        className={`relative w-full mx-4 flex flex-col items-center justify-center ${isFullscreen ? 'h-screen bg-black' : 'max-w-4xl max-h-[90vh]'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className={`absolute right-2 text-white text-3xl hover:text-gray-300 z-10 ${isFullscreen ? 'top-2' : '-top-10'}`}
          aria-label="Close video"
        >
          ×
        </button>
        
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
            <div className="relative flex items-center justify-center">
              <svg className="w-28 h-28 transform -rotate-90" viewBox="0 0 100 100">
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  stroke="#374151"
                  strokeWidth="6"
                  fill="none"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  stroke="#3B82F6"
                  strokeWidth="6"
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeDashoffset}
                  className="transition-all duration-300"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-white text-xl font-semibold">{loadProgress}%</span>
              </div>
            </div>
          </div>
        )}
        
        {blobUrl && (
          <video
            ref={videoRef}
            src={blobUrl}
            controls
            autoPlay
            playsInline
            controlsList="nofullscreen"
            className={`rounded-lg ${isFullscreen ? 'w-auto h-full max-h-screen' : 'w-full max-h-[80vh]'}`}
            style={{ backgroundColor: '#000', objectFit: 'contain' }}
          >
            Your browser does not support the video tag.
          </video>
        )}
        <button
          onClick={toggleFullscreen}
          className={`absolute ${isFullscreen ? 'bottom-4 right-4' : 'bottom-2 right-2'} bg-black bg-opacity-60 hover:bg-opacity-80 text-white px-3 py-2 rounded-lg flex items-center gap-2 z-10`}
          aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
        >
          {isFullscreen ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"/>
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
            </svg>
          )}
          <span className="text-sm">{isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}</span>
        </button>
      </div>
    </div>
  );
};

const ConImageList = (props) => {
  const taskId = props.taskId;
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageCache, setImageCache] = useState({});
  const [activeVideo, setActiveVideo] = useState(null);

  const todayViewerContainerRef = useRef(null);
  const historyViewerContainerRef = useRef(null);
  const todayViewerInstanceRef = useRef(null);
  const historyViewerInstanceRef = useRef(null);

  const preloadImage = useCallback((url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(url);
      img.onerror = reject;
      img.src = url;
    });
  }, []);

  useEffect(() => {
    if (!taskId) return;
    const fetchMedia = async () => {
      try {
        setLoading(true);
        const response = await fetch(`/api/task/${taskId}`);
        if (!response.ok) throw new Error('Failed to fetch task data');
        const taskData = await response.json();
        const fetchedMedia = taskData.images || [];
        setMedia(fetchedMedia);

        const imagesToPreload = fetchedMedia.filter(m => isImage(m)).slice(0, 3);
        if (imagesToPreload.length > 0) {
          const preloadPromises = imagesToPreload.map(img => {
            const imageUrl = img.image_url || img.image_path;
            if (imageUrl && !imageCache[imageUrl]) {
              return preloadImage(imageUrl).then(() => {
                setImageCache(prev => ({ ...prev, [imageUrl]: true }));
              }).catch(err => console.error("Preload failed:", err));
            }
            return Promise.resolve();
          });
          await Promise.all(preloadPromises);
        }
      } catch (err) {
        setError('Failed to load task data');
      } finally {
        setLoading(false);
      }
    };
    fetchMedia();
  }, [taskId, imageCache, preloadImage]);

  useEffect(() => {
    const loadViewerJS = () => {
      if (document.getElementById('viewerjs-css') || document.getElementById('viewerjs-js')) {
        initializeViewers();
        return;
      }

      const link = document.createElement('link');
      link.id = 'viewerjs-css';
      link.rel = 'stylesheet';
      link.href = '/viewerjs/viewer.min.css';
      document.head.appendChild(link);

      const script = document.createElement('script');
      script.id = 'viewerjs-js';
      script.src = '/viewerjs/viewer.min.js';
      script.onload = () => {
        initializeViewers();
      };
      document.head.appendChild(script);
    };

    const initializeViewers = () => {
      if (!window.Viewer || media.length === 0) return;

      if (todayViewerInstanceRef.current) {
        todayViewerInstanceRef.current.destroy();
      }
      if (historyViewerInstanceRef.current) {
        historyViewerInstanceRef.current.destroy();
      }

      const todayImages = media.filter(m => isToday(m.upload_time || m.upload_date) && isImage(m));
      const historyImages = media.filter(m => !isToday(m.upload_time || m.upload_date) && isImage(m));

      setTimeout(() => {
        if (todayViewerContainerRef.current && todayImages.length > 0) {
          const imageElements = todayViewerContainerRef.current.querySelectorAll('img[data-viewer-image="true"]');
          if (imageElements.length > 0) {
            todayViewerInstanceRef.current = new window.Viewer(todayViewerContainerRef.current, {
              inline: false,
              navbar: true,
              title: true,
              filter: (image) => image.dataset.viewerImage === 'true',
              toolbar: {
                zoomIn: true,
                zoomOut: true,
                oneToOne: true,
                reset: true,
                prev: true,
                play: false,
                next: true,
                rotateLeft: true,
                rotateRight: true,
                flipHorizontal: false,
                flipVertical: false,
              },
              url(image) {
                const srcAttr = image.getAttribute('src');
                if (srcAttr && srcAttr.includes('thumbnail=true')) {
                  return srcAttr.replace(/\?thumbnail=true&width=\d+/, '');
                }
                return srcAttr;
              },
              loading: true,
            });
          }
        }

        if (historyViewerContainerRef.current && historyImages.length > 0) {
          const imageElements = historyViewerContainerRef.current.querySelectorAll('img[data-viewer-image="true"]');
          if (imageElements.length > 0) {
            historyViewerInstanceRef.current = new window.Viewer(historyViewerContainerRef.current, {
              inline: false,
              navbar: true,
              title: true,
              filter: (image) => image.dataset.viewerImage === 'true',
              toolbar: {
                zoomIn: true,
                zoomOut: true,
                oneToOne: true,
                reset: true,
                prev: true,
                play: false,
                next: true,
                rotateLeft: true,
                rotateRight: true,
                flipHorizontal: false,
                flipVertical: false,
              },
              url(image) {
                const srcAttr = image.getAttribute('src');
                if (srcAttr && srcAttr.includes('thumbnail=true')) {
                  return srcAttr.replace(/\?thumbnail=true&width=\d+/, '');
                }
                return srcAttr;
              },
              loading: true,
            });
          }
        }
      }, 100);
    };

    if (!loading && media.length > 0) {
      loadViewerJS();
    }

    return () => {
      if (todayViewerInstanceRef.current) {
        todayViewerInstanceRef.current.destroy();
        todayViewerInstanceRef.current = null;
      }
      if (historyViewerInstanceRef.current) {
        historyViewerInstanceRef.current.destroy();
        historyViewerInstanceRef.current = null;
      }
    };
  }, [media, loading]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  const todayMedia = media.filter(m => isToday(m.upload_time || m.upload_date));
  const historyMedia = media.filter(m => !isToday(m.upload_time || m.upload_date));

  const sortedHistoryMedia = [...historyMedia].sort((a, b) => {
    const dateA = a.upload_time || a.upload_date;
    const dateB = b.upload_time || b.upload_date;

    if (!dateA && !dateB) return 0;
    if (!dateA) return 1;
    if (!dateB) return -1;

    return new Date(dateB) - new Date(dateA);
  });

  const getTorontoDateString = (dateStr) => {
    if (!dateStr) return 'Unknown Date';
    let adjustedDateStr = dateStr;
    if (!dateStr.includes('+') && !dateStr.includes('Z')) {
      adjustedDateStr = dateStr + 'Z';
    }
    const date = new Date(adjustedDateStr);
    if (isNaN(date.getTime())) return 'Unknown Date';
    return date.toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });
  };

  const groupMediaByDate = (mediaList) => {
    const groups = {};
    
    mediaList.forEach(item => {
      const dateStr = item.upload_time || item.upload_date;
      const torontoDate = getTorontoDateString(dateStr);
      
      if (!groups[torontoDate]) {
        groups[torontoDate] = [];
      }
      groups[torontoDate].push(item);
    });
    
    const sortedDates = Object.keys(groups).sort((a, b) => b.localeCompare(a));
    
    return sortedDates.map(date => ({
      date,
      items: groups[date]
    }));
  };

  const groupedHistoryMedia = groupMediaByDate(sortedHistoryMedia);

  const getMediaUrl = (item, index, isThumbnail = true) => {
    // Always use same-origin Flask proxy — private S3 URLs return 403 in the browser.
    if (taskId && index !== undefined) {
      const thumbnailParam = isThumbnail && isImage(item) ? '?thumbnail=true&width=200' : '';
      return `/api/media/construction/task/${taskId}/${index}${thumbnailParam}`;
    }
    return '/images/no-image.png';
  };

  const getUploader = (item) => item.upload_by_name || item.upload_by || item.user_name || item.user_id || '';

  const getUploadTime = (item) => {
    const t = item.upload_time || item.upload_date;
    if (!t) return "-";

    let dateStr = t;
    if (!t.includes('+') && !t.includes('Z')) {
      dateStr = t + 'Z';
    }

    return new Date(dateStr).toLocaleString("en-CA", {
      timeZone: "America/Toronto",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const handleMediaClick = (item, index) => {
    if (isVideo(item)) {
      const videoUrl = getMediaUrl(item, index, false);
      setActiveVideo(videoUrl);
    }
  };

  const handleImageLoad = (item) => {
    const itemIsToday = isToday(item.upload_time || item.upload_date);
    if (itemIsToday && todayViewerInstanceRef.current) {
      todayViewerInstanceRef.current.update();
    } else if (!itemIsToday && historyViewerInstanceRef.current) {
      historyViewerInstanceRef.current.update();
    }
  };

  const renderMediaItem = (item, idx, mediaList) => {
    const realIndex = media.findIndex(fullItem => fullItem === item);
    const itemIsVideo = isVideo(item);
    const itemIsImage = isImage(item);

    return (
      <div 
        key={idx} 
        style={{ 
          border: '1px solid #eee', 
          borderRadius: 8, 
          padding: 8, 
          width: 180, 
          background: '#fafbfc', 
          position: 'relative' 
        }}
      >
        {itemIsVideo ? (
          <div 
            className="relative cursor-pointer group"
            onClick={() => handleMediaClick(item, realIndex)}
          >
            <div 
              style={{ 
                width: '100%', 
                height: 120, 
                background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <div 
                style={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: 4,
                  padding: '2px 6px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4
                }}
              >
                <FaVideo size={10} color="#fff" />
                <span style={{ fontSize: 10, color: '#fff' }}>VIDEO</span>
              </div>
              <div 
                className="group-hover:scale-110 transition-transform"
                style={{
                  width: 50,
                  height: 50,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.9)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }}
              >
                <FaPlay size={20} color="#1a1a2e" style={{ marginLeft: 4 }} />
              </div>
            </div>
          </div>
        ) : itemIsImage ? (
          <LazyImage
            src={getMediaUrl(item, realIndex, true)}
            dataSrc={getMediaUrl(item, realIndex, false)}
            alt={item.description || "Task image"}
            style={{ 
              width: '100%', 
              height: 120, 
              objectFit: 'cover', 
              cursor: 'pointer', 
              background: '#f5f5f5',
              borderRadius: 4
            }}
            crossOrigin="anonymous"
            onError={(e) => {
              const el = e.target;
              if (!el) return;
              el.src = '/images/no-image.png';
              el.style.opacity = '0.5';
            }}
            onLoad={(e) => {
              handleImageLoad(item);
            }}
          />
        ) : (
          <div 
            style={{ 
              width: '100%', 
              height: 120, 
              background: '#f0f0f0',
              borderRadius: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <span style={{ color: '#999', fontSize: 12 }}>Unknown format</span>
          </div>
        )}
        
        <div style={{ fontSize: 11, marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
          {itemIsVideo ? (
            <FaVideo size={10} color="#6366f1" />
          ) : (
            <FaImage size={10} color="#22c55e" />
          )}
          <span>{getUploadTime(item)}</span>
        </div>
        <div style={{ fontSize: 11, color: '#666' }}>By: {getUploader(item)}</div>
      </div>
    );
  };

  return (
    <div style={{ maxHeight: '80vh', minWidth: 350, maxWidth: 900, overflowY: 'auto', padding: 24, position: 'relative', background: 'white', borderRadius: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.12)' }}>
      {props.onClose && (
        <button
          onClick={props.onClose}
          style={{ position: 'absolute', top: 12, right: 16, fontSize: 24, color: '#888', background: 'none', border: 'none', cursor: 'pointer', zIndex: 10 }}
          aria-label="Close"
        >×</button>
      )}
      <h2 className="text-2xl font-bold mb-4">Task Media</h2>

      <div className="mb-2 font-semibold flex items-center gap-2">
        <span>Today's Media</span>
        <span className="text-xs text-gray-400">
          ({todayMedia.filter(m => isImage(m)).length} images, {todayMedia.filter(m => isVideo(m)).length} videos)
        </span>
      </div>
      <div ref={todayViewerContainerRef} style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
        {todayMedia.length === 0 && <div className="text-gray-400">No media today.</div>}
        {todayMedia.map((item, idx) => renderMediaItem(item, idx, todayMedia))}
      </div>

      <div className="mt-4 mb-2 font-semibold flex items-center gap-2">
        <span>History Media</span>
        <span className="text-xs text-gray-400">
          ({sortedHistoryMedia.filter(m => isImage(m)).length} images, {sortedHistoryMedia.filter(m => isVideo(m)).length} videos)
        </span>
      </div>
      <div ref={historyViewerContainerRef}>
        {sortedHistoryMedia.length === 0 && <div className="text-gray-400">No history media.</div>}
        {groupedHistoryMedia.map(group => (
          <div key={group.date} className="mb-4">
            <div className="font-medium text-gray-600 mb-2 flex items-center gap-2 border-b border-gray-200 pb-1">
              <span>{group.date}</span>
              <span className="text-xs text-gray-400">
                ({group.items.filter(m => isImage(m)).length} images, {group.items.filter(m => isVideo(m)).length} videos)
              </span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
              {group.items.map((item, idx) => renderMediaItem(item, idx, group.items))}
            </div>
          </div>
        ))}
      </div>

      {activeVideo && (
        <VideoPlayerModal 
          videoUrl={activeVideo} 
          onClose={() => setActiveVideo(null)} 
        />
      )}
    </div>
  );
};

export default ConImageList;
export { LazyImage, isImage, isVideo, VideoPlayerModal };
